import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { FaFilter, FaSearch, FaArrowLeft, FaBook, FaGlobe, FaLanguage, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import allWords from '../data/json/combined_fin_vocab.json';

const WordList = () => {
    const [categoryFilter, setCategoryFilter] = useState("");
    const [partOfSpeechFilter, setPartOfSpeechFilter] = useState("");
    const [difficultyFilter, setDifficultyFilter] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState(null);

    const wordsPerPage = 20;

    if (!allWords || allWords.length === 0) {
        return (
            <div className="modern-container">
                <div className="modern-section">
                    <div className="modern-alert modern-alert-danger fade-in-up">
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-md)' }}>
                                ⚠️
                            </div>
                            <h4>単語データが見つかりませんでした</h4>
                            <Link to="/" className="modern-btn modern-btn-primary">
                                ホームに戻る
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const categoryOptions = [...new Set(allWords.map(word => word.category).filter(Boolean))];
    const partOfSpeechOptions = [...new Set(allWords.map(word => word.part_of_speech))];
    const difficultyOptions = [...new Set(allWords.map(word => word.difficulty))];

    const filteredWords = allWords.filter(word =>
        (categoryFilter === "" || word.category === categoryFilter) &&
        (partOfSpeechFilter === "" || word.part_of_speech === partOfSpeechFilter) &&
        (difficultyFilter === "" || word.difficulty === difficultyFilter) &&
        (searchQuery === "" || 
            word.word.toLowerCase().includes(searchQuery.toLowerCase()) ||
            word.japanese.includes(searchQuery) ||
            word.english.toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const indexOfLastWord = currentPage * wordsPerPage;
    const indexOfFirstWord = indexOfLastWord - wordsPerPage;
    const currentWords = filteredWords.slice(indexOfFirstWord, indexOfFirstWord + wordsPerPage);

    const totalPages = Math.ceil(filteredWords.length / wordsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleRowClick = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    const resetFilters = () => {
        setCategoryFilter("");
        setPartOfSpeechFilter("");
        setDifficultyFilter("");
        setSearchQuery("");
        setCurrentPage(1);
    };

    const getBadgeColor = (partOfSpeech, difficulty) => {
        if (partOfSpeech === '名詞') return 'primary';
        if (partOfSpeech === '動詞') return 'success';
        if (partOfSpeech === '形容詞') return 'warning';
        return 'info';
    };

    const getDifficultyColor = (difficulty) => {
        if (difficulty === '初級') return 'success';
        if (difficulty === '中級') return 'warning';
        return 'danger';
    };

    return (
        <div className="modern-container">
            <div className="modern-section">
                {/* Header */}
                <div className="fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                    <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-md)',
                        marginBottom: 'var(--spacing-lg)'
                    }}>
                        <Link 
                            to="/" 
                            className="modern-btn modern-btn-secondary"
                            style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-xs)',
                                padding: 'var(--spacing-sm) var(--spacing-md)'
                            }}
                        >
                            <FaArrowLeft />
                            戻る
                        </Link>
                        <span className="modern-badge modern-badge-info">
                            {filteredWords.length} / {allWords.length} 単語
                        </span>
                    </div>
                    
                    <h2 style={{ 
                        background: 'var(--info-gradient)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: 'var(--spacing-md)',
                        textAlign: 'center'
                    }}>
                        📚 単語一覧
                    </h2>
                    
                    <div className="modern-alert modern-alert-info fade-in-up">
                        <FaBook />
                        <div>
                            このページでは、検索やフィルタを使用して単語を絞り込むことができます。各単語をクリックすると、例文が表示されます。
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="modern-card fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                    <div className="modern-card-header">
                        <h5 style={{ 
                            margin: 0, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-sm)' 
                        }}>
                            <FaFilter />
                            検索・絞り込み
                        </h5>
                    </div>
                    <div className="modern-card-body">
                        {/* Search Bar */}
                        <div style={{ 
                            position: 'relative',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            <FaSearch style={{
                                position: 'absolute',
                                left: 'var(--spacing-md)',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'var(--text-muted)',
                                zIndex: 1
                            }} />
                            <input
                                type="text"
                                placeholder="単語を検索（フィンランド語、日本語、英語）"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{
                                    width: '100%',
                                    padding: 'var(--spacing-md) var(--spacing-md) var(--spacing-md) 2.5rem',
                                    border: '2px solid var(--border-color)',
                                    borderRadius: 'var(--radius-lg)',
                                    fontSize: '1rem',
                                    transition: 'border-color var(--transition-fast)',
                                    backgroundColor: 'var(--surface)'
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                                onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                            />
                        </div>

                        {/* Filter Grid */}
                        <div style={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            <select
                                value={categoryFilter}
                                onChange={(e) => {
                                    setCategoryFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{
                                    padding: 'var(--spacing-md)',
                                    border: '2px solid var(--border-color)',
                                    borderRadius: 'var(--radius-lg)',
                                    backgroundColor: 'var(--surface)',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="">すべてのカテゴリ</option>
                                {categoryOptions.map((category, index) => (
                                    <option key={index} value={category}>{category}</option>
                                ))}
                            </select>

                            <select
                                value={partOfSpeechFilter}
                                onChange={(e) => {
                                    setPartOfSpeechFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{
                                    padding: 'var(--spacing-md)',
                                    border: '2px solid var(--border-color)',
                                    borderRadius: 'var(--radius-lg)',
                                    backgroundColor: 'var(--surface)',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="">すべての品詞</option>
                                {partOfSpeechOptions.map((pos, index) => (
                                    <option key={index} value={pos}>{pos}</option>
                                ))}
                            </select>

                            <select
                                value={difficultyFilter}
                                onChange={(e) => {
                                    setDifficultyFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{
                                    padding: 'var(--spacing-md)',
                                    border: '2px solid var(--border-color)',
                                    borderRadius: 'var(--radius-lg)',
                                    backgroundColor: 'var(--surface)',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="">すべての難易度</option>
                                {difficultyOptions.map((difficulty, index) => (
                                    <option key={index} value={difficulty}>{difficulty}</option>
                                ))}
                            </select>
                        </div>

                        <button 
                            onClick={resetFilters} 
                            className="modern-btn modern-btn-secondary"
                        >
                            フィルタをリセット
                        </button>
                    </div>
                </div>

                {/* Words List */}
                <div className="modern-card fade-in-up">
                    <div className="modern-card-header">
                        <h5 style={{ margin: 0 }}>
                            単語リスト ({indexOfFirstWord + 1}-{Math.min(indexOfLastWord, filteredWords.length)} / {filteredWords.length})
                        </h5>
                    </div>
                    <div style={{ overflow: 'auto' }}>
                        {currentWords.length === 0 ? (
                            <div style={{ 
                                padding: 'var(--spacing-2xl)',
                                textAlign: 'center',
                                color: 'var(--text-muted)'
                            }}>
                                <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-md)' }}>
                                    🔍
                                </div>
                                <h4>該当する単語が見つかりませんでした</h4>
                                <p>検索条件やフィルタを変更してお試しください。</p>
                            </div>
                        ) : (
                            currentWords.map((word, index) => (
                                <div key={index}>
                                    <div 
                                        style={{
                                            padding: 'var(--spacing-lg)',
                                            borderBottom: '1px solid var(--border-color)',
                                            cursor: 'pointer',
                                            transition: 'background-color var(--transition-fast)',
                                            display: 'grid',
                                            gridTemplateColumns: '1fr auto',
                                            gap: 'var(--spacing-md)',
                                            alignItems: 'center'
                                        }}
                                        onClick={() => handleRowClick(index)}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--surface-hover)'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                    >
                                        <div>
                                            <div style={{ 
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--spacing-md)',
                                                marginBottom: 'var(--spacing-sm)'
                                            }}>
                                                <h5 style={{ 
                                                    color: 'var(--text-primary)',
                                                    margin: 0,
                                                    fontSize: '1.25rem'
                                                }}>
                                                    {word.word}
                                                </h5>
                                                <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                                                    <span className={`modern-badge modern-badge-${getBadgeColor(word.part_of_speech)}`}>
                                                        {word.part_of_speech}
                                                    </span>
                                                    {word.difficulty && (
                                                        <span className={`modern-badge modern-badge-${getDifficultyColor(word.difficulty)}`}>
                                                            {word.difficulty}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div style={{ 
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                                gap: 'var(--spacing-md)',
                                                color: 'var(--text-secondary)'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                                                    <FaLanguage size={14} />
                                                    <strong>日本語:</strong> {word.japanese}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                                                    <FaGlobe size={14} />
                                                    <strong>English:</strong> {word.english}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div style={{ 
                                            color: 'var(--text-muted)',
                                            fontSize: '1.25rem'
                                        }}>
                                            {expandedRow === index ? <FaChevronDown /> : <FaChevronRight />}
                                        </div>
                                    </div>
                                    
                                    <Collapse in={expandedRow === index}>
                                        <div style={{ 
                                            padding: 'var(--spacing-lg)',
                                            backgroundColor: 'var(--background-primary)',
                                            borderBottom: '1px solid var(--border-color)',
                                            fontStyle: 'italic',
                                            color: 'var(--text-secondary)'
                                        }}>
                                            <strong>例文:</strong> {word.example || "例文がありません。"}
                                        </div>
                                    </Collapse>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)',
                        marginTop: 'var(--spacing-2xl)'
                    }} className="fade-in-up">
                        <button 
                            onClick={() => paginate(1)} 
                            disabled={currentPage === 1}
                            className="modern-btn modern-btn-secondary"
                            style={{ 
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                opacity: currentPage === 1 ? 0.5 : 1
                            }}
                        >
                            最初
                        </button>
                        <button 
                            onClick={() => paginate(currentPage - 1)} 
                            disabled={currentPage === 1}
                            className="modern-btn modern-btn-secondary"
                            style={{ 
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                opacity: currentPage === 1 ? 0.5 : 1
                            }}
                        >
                            前へ
                        </button>
                        
                        <div style={{ 
                            display: 'flex',
                            gap: 'var(--spacing-xs)'
                        }}>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                                const pageNumber = startPage + i;
                                
                                return (
                                    <button
                                        key={pageNumber}
                                        onClick={() => paginate(pageNumber)}
                                        className={`modern-btn ${pageNumber === currentPage ? 'modern-btn-primary' : 'modern-btn-secondary'}`}
                                        style={{ 
                                            padding: 'var(--spacing-sm) var(--spacing-md)',
                                            minWidth: '40px'
                                        }}
                                    >
                                        {pageNumber}
                                    </button>
                                );
                            })}
                        </div>
                        
                        <button 
                            onClick={() => paginate(currentPage + 1)} 
                            disabled={currentPage === totalPages}
                            className="modern-btn modern-btn-secondary"
                            style={{ 
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                opacity: currentPage === totalPages ? 0.5 : 1
                            }}
                        >
                            次へ
                        </button>
                        <button 
                            onClick={() => paginate(totalPages)} 
                            disabled={currentPage === totalPages}
                            className="modern-btn modern-btn-secondary"
                            style={{ 
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                opacity: currentPage === totalPages ? 0.5 : 1
                            }}
                        >
                            最後
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WordList;
